import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {CheckRuleControllerService} from '../api/api/checkRuleController.service';

export type MyCheckProposalKind = 'KI' | 'IDS' | 'HYBRID';
export type MyCheckProposalStatus = 'PENDING' | 'APPLIED' | 'DISCARDED' | 'SUPERSEDED';

/** Ein einzelner Feld-Änderungshinweis im Vorschlag, für den Banner. */
export interface MyCheckProposalChangeNote {
  path: string;
  reason?: string;
}

/**
 * Ein vom Agenten im Edit-Modus erzeugter Änderungsvorschlag für ein myCheck.
 *
 * Spiegelt `MyCheckProposalController.ProposalResponse` im Backend; `document` und `changeNotes`
 * sind hier bereits aus den dort als JSON-**Strings** transportierten `documentJson`/
 * `changeNotesJson` geparst.
 */
export interface MyCheckProposal {
  id: string;
  rulesetId: string;
  kind: MyCheckProposalKind;
  projectId: number;
  createdByUserId?: number;
  conversationId?: string;
  summary?: string;
  changeNotes: MyCheckProposalChangeNote[];
  /** Struktur hängt von `kind` ab. */
  document: any;
  baseFingerprint?: string;
  status: MyCheckProposalStatus;
  createdAt?: Date;
  resolvedAt?: Date;
  /** true, wenn der Nutzer den myCheck seit `baseFingerprint` bereits selbst verändert hat. */
  stale: boolean;
  currentFingerprint?: string;
}

/**
 * Zugriff auf den Proposal-Store des Backends (`/check-rules/{rulesetId}/proposals`, siehe B1).
 *
 * <p>Ruft die Endpunkte direkt über `HttpClient` auf statt über einen generierten Client – derselbe,
 * bereits etablierte Ansatz wie in {@link RulesetService} für Endpunkte außerhalb des dortigen
 * Kern-Codegens (z.B. `findMyCheckUsages`, `createIdsRule`). `basePath` wird wie dort von einem
 * generierten Service abgeleitet, um die per-Environment konfigurierte Backend-URL nicht zu duplizieren.
 */
@Injectable({providedIn: 'root'})
export class MyCheckProposalService {
  private http = inject(HttpClient);
  private checkRuleApi = inject(CheckRuleControllerService);

  private get basePath(): string {
    return (this.checkRuleApi as any).basePath ?? '';
  }

  /** Der aktuell offene (PENDING) Vorschlag für dieses myCheck, oder `null`, wenn keiner ansteht. */
  getLatestPending(rulesetId: string, projectId: number): Observable<MyCheckProposal | null> {
    return this.http.get<any>(this.urlFor(rulesetId, '/latest'), {params: {projectId: String(projectId)}}).pipe(
      map(raw => this.toProposal(raw)),
      catchError(error => this.notFoundToNull(error)),
    );
  }

  getById(rulesetId: string, proposalId: string, projectId: number): Observable<MyCheckProposal> {
    return this.http.get<any>(this.urlFor(rulesetId, `/${proposalId}`), {params: {projectId: String(projectId)}}).pipe(
      map(raw => this.toProposal(raw)!),
    );
  }

  /**
   * Markiert den Vorschlag als übernommen. Speichert **nicht** selbst den myCheck – das bleibt dem
   * bestehenden Speichern-Pfad des jeweiligen Editors (PUT) überlassen.
   */
  apply(rulesetId: string, proposalId: string, projectId: number): Observable<MyCheckProposal> {
    return this.http.post<any>(this.urlFor(rulesetId, `/${proposalId}/apply`), {}, {params: {projectId: String(projectId)}}).pipe(
      map(raw => this.toProposal(raw)!),
    );
  }

  /** Verwirft den Vorschlag; der myCheck bleibt unverändert. */
  discard(rulesetId: string, proposalId: string, projectId: number): Observable<MyCheckProposal> {
    return this.http.post<any>(this.urlFor(rulesetId, `/${proposalId}/discard`), {}, {params: {projectId: String(projectId)}}).pipe(
      map(raw => this.toProposal(raw)!),
    );
  }

  private urlFor(rulesetId: string, suffix: string): string {
    return `${this.basePath}/check-rules/${rulesetId}/proposals${suffix}`;
  }

  /** 404 (kein PENDING-Vorschlag) → `null`; alle anderen Fehler (403, 500, …) werden durchgereicht. */
  private notFoundToNull(error: unknown): Observable<null> {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }

  private toProposal(raw: any): MyCheckProposal | null {
    if (!raw) return null;
    return {
      id: raw.id,
      rulesetId: raw.rulesetId,
      kind: raw.kind,
      projectId: raw.projectId,
      createdByUserId: raw.createdByUserId ?? undefined,
      conversationId: raw.conversationId ?? undefined,
      summary: raw.summary ?? undefined,
      changeNotes: this.parseJsonArray(raw.changeNotesJson),
      document: this.parseJsonObject(raw.documentJson),
      baseFingerprint: raw.baseFingerprint ?? undefined,
      status: raw.status,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : undefined,
      resolvedAt: raw.resolvedAt ? new Date(raw.resolvedAt) : undefined,
      stale: !!raw.stale,
      currentFingerprint: raw.currentFingerprint ?? undefined,
    };
  }

  private parseJsonObject(text: string | null | undefined): any {
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  private parseJsonArray(text: string | null | undefined): MyCheckProposalChangeNote[] {
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
